import { useEffect, useMemo } from "react";
import { setOptions } from "@mobiscroll/react";
import { getI18n, useTranslation } from "react-i18next";
import { useColorScheme } from "@mui/material/styles";
import { Box } from "@mui/material";

import { initializeSchedulerStore } from "./helpers/init";
import { useSchedulerStore } from "./state/scheduler.store";

import { Header } from "./components/Header/Header";
import Dialogs from "./components/Dialogs/Dialogs";
import ViewManager from "./components/ViewManager/ViewManager";

import { useAvailability } from "./hooks/useAvailability";
import { usePreviousSlot } from "./hooks/usePreviousSlot";
import { useMeetingLinkTimezone } from "./hooks/useMeetingLinkTimezone";
import { useRoosterLinkValidation } from "./hooks/useRoosterLinkValidation";

import "../src/assets/mobiscroll.css";
import { setTermsAndConditions } from "./helpers/meeting/termsandconditions";

function Scheduler() {
  getI18n();
  const { t } = useTranslation();
  const { mode } = useColorScheme();
  const { actions, clientId, meetingLink, sessionData, trackingId } =
    useSchedulerStore();
  const {
    validateLink: validateRoosterLink,
    setTnC: setTermsAndConditionForRoosterLinks
  } = useRoosterLinkValidation();
  const { currentTimezone } = useMeetingLinkTimezone();
  const { previouslySelectedTime } = usePreviousSlot();
  const { fetchAvailability } = useAvailability();

  const isRoosterLink = useMemo(() => {
    return meetingLink?.type === "rooster_links";
  }, [meetingLink?.type]);

  // update mobiscroll theme based on current color scheme
  useEffect(() => {
    setOptions({
      theme: "ios",
      themeVariant: mode === "system" ? "auto" : mode
    });
  }, [mode]);

  // initialize app state
  useEffect(() => {
    const initialize = async () => {
      await initializeSchedulerStore(actions, t);
    };
    initialize();
  }, []);

  // validate link and set terms and conditions for rooster links
  useEffect(() => {
    if (clientId && trackingId) {
      if (isRoosterLink) {
        validateRoosterLink();
        setTermsAndConditionForRoosterLinks();
      } else {
        setTermsAndConditions(clientId, actions);
      }
    }
  }, [clientId, isRoosterLink, trackingId]);

  // load availability for rooster link or autobook
  useEffect(() => {
    const loadAvailability = async () => {
      !sessionData?.expiredLink && (await fetchAvailability());
    };
    loadAvailability();
  }, [
    currentTimezone,
    previouslySelectedTime,
    sessionData?.requestSessionStartedAt
  ]);

  return (
    <>
      {/* 
      - Skip Navigation Link - EAA Required 
      - A "Skip to main content" link allows keyboard and screen reader users 
      to bypass navigation and jump directly to the main content, meeting EAA accessibility requirements.
      */}
      <nav aria-label="Skip links">
        <a href="#main-content" className="skip-link">
          {t("ROOSTER.PUBLIC-APP.SKIP_TO_CONTENT", {
            defaultValue: "Skip to main content"
          })}
        </a>
      </nav>
      <Dialogs />
      <Header />
      <Box component="main" id="main-content" tabIndex={-1}>
        <ViewManager />
      </Box>
    </>
  );
}

export default Scheduler;
